import Axios from 'axios';
import { useContext, useEffect, useRef } from 'react';
import useSWR, { type SWRConfiguration, type SWRResponse } from 'swr';

import { ErrorContext } from '@/context/ErrorContext';
import ApiError from '@/helpers/ApiError';
import { buildApiErrorFingerprint, defaultErrorToastMessage, getRoutePath } from '@/hooks/useNoodleApi/util';
import useToast from '@/hooks/useToast';
import { type ErrorFlow, type SeverityLevel, logError } from '@/providers/ErrorTracking';
import { SystemHealthMonitor, type TransactionNames } from '@/providers/SystemHealth';
import { type AxiosKey, noodleApiFetcher } from '@/tsClient/servers/noodleApi';

import type { ToastErrorMessage, ToastSuccessMessage } from './typings';

type UseNoodleSwrConfig<Data = unknown, Err = ApiError> = SWRConfiguration<Data, Err> &
  Partial<{
    errorLevel: SeverityLevel;
    errorFlow: ErrorFlow;
    toastOnError: boolean;
    toastErrorMessage: ToastErrorMessage;
    toastSuccessMessage: ToastSuccessMessage;
    reportError: boolean;
    showErrorScreen: boolean;
    healthMonitor: {
      name: TransactionNames;
    };
  }>;

type SuccessHandler<Data = unknown, Err = ApiError> = SWRConfiguration<Data, Err>['onSuccess'];
type ErrorHandler<Data = unknown, Err = ApiError> = SWRConfiguration<Data, Err>['onError'];
type SwrKey = AxiosKey | null;

export const useNoodleSwr = <Data = unknown, Err = ApiError>(
  key: SwrKey,
  {
    errorLevel,
    errorFlow,
    toastOnError = false,
    toastErrorMessage = defaultErrorToastMessage,
    toastSuccessMessage,
    reportError = true,
    showErrorScreen = false,
    healthMonitor,
    onError,
    onSuccess,
    // Fetcher is intentionally ignored; we always use noodleApiFetcher.
    fetcher: _fetcher,
    // Other SWR options pass through as-is.
    ...rest
  }: UseNoodleSwrConfig<Data, Err> = {},
): SWRResponse<Data, Err> => {
  const addToast = useToast();
  const { setError } = useContext(ErrorContext);
  const requestingPage = useRef<string>('unknown page');
  requestingPage.current = typeof window !== 'undefined' ? window.location?.href || 'unknown page' : 'unknown page';

  const monitorRef = useRef<SystemHealthMonitor | null>(null);

  useEffect(() => {
    if (healthMonitor?.name) {
      monitorRef.current = new SystemHealthMonitor(healthMonitor.name);
    } else {
      monitorRef.current = null;
    }
  }, [healthMonitor]);

  const handleSuccess: SuccessHandler<Data, Err> = (data, keyArg, config): void => {
    if (toastSuccessMessage) {
      addToast(...toastSuccessMessage(data));
    }
    monitorRef.current?.finish();
    onSuccess?.(data, keyArg, config);
  };

  const handleError: ErrorHandler<Data, Err> = (originalError, keyArg, config): void => {
    const error = ApiError.create(originalError);

    if (reportError) {
      const isReportedOnServer = error.statusCode < 500;
      const errors = error?.errors || [];
      const errorDataObject = typeof error === 'string' ? { data: error } : error;
      const axiosErrorContext = Axios.isAxiosError(originalError)
        ? {
            baseUrl: originalError.config?.baseURL,
            method: originalError.config?.method,
            url: originalError.config?.url,
          }
        : {};
      const functionName = 'useNoodleSwr';
      const errorType = error?.type || 'UnknownError';
      const errorMessage = error.message || 'UnknownError';
      const currentPage = typeof window !== 'undefined' ? window.location?.href || 'unknown page' : 'unknown page';
      const routePath = getRoutePath(originalError);
      const httpMethod = Axios.isAxiosError(originalError) ? originalError.config?.method?.toUpperCase() : undefined;
      const tags = errorFlow ? undefined : { flow: errorFlow };
      let level = errorLevel;
      if (!level && isReportedOnServer) {
        level = 'info';
      }

      const fingerprint = buildApiErrorFingerprint({
        errorType,
        httpMethod,
        routePath,
        source: 'useNoodleSwr',
        statusCode: error.statusCode,
      });

      logError(error, {
        currentPage,
        errorMessage,
        errorType,
        errors,
        fingerprint,
        functionName,
        handledBy: 'useNoodleSwr',
        level,
        requestingPage: requestingPage.current,
        routePath,
        tags,
        ...errorDataObject,
        ...axiosErrorContext,
      });
    }

    if (toastOnError) {
      addToast(...toastErrorMessage(error));
    }

    if (showErrorScreen) {
      setError(error);
    }

    monitorRef.current?.finish(error);

    onError?.(originalError, keyArg, config);
  };

  const fetcher = async (fetchKey: AxiosKey): Promise<Data> => {
    try {
      return await noodleApiFetcher<Data>(fetchKey);
    } catch (originalError) {
      throw ApiError.create(originalError);
    }
  };

  return useSWR<Data, Err>(key, fetcher, {
    ...rest,
    onError: handleError,
    onSuccess: handleSuccess,
  });
};

export type { UseNoodleSwrConfig, SwrKey };
